import type {
  ArenaCrowdDef,
  ArenaGrabDef,
  ArenaRulesDef,
  ArenaTemplateDef,
  LureSiteRulesDef,
  OverwatchDef,
  OverwatchFigureDef,
  OverwatchMissionDef,
  OverwatchPerchDef,
  OverwatchPhaseDef,
  OverwatchRulesDef,
  OverwatchSceneDef,
  OverwatchSpawnDef,
  VehicleClingDef,
  VehicleDef,
  VehicleFuelDef,
  VehicleNoiseDef,
  VehicleRamDef,
  VehicleMountDef,
  ScenarioArenaDef,
} from './types';

/*
 * The C# initializers of the Step 3 content classes (packages/shared-cs/Runtime/Content: VehicleDefs.cs, ArenaDefs.cs, OverwatchDefs.cs).
 * The server parses a bundle into those classes, so a field missing from the JSON gets these values there; the forms show them for a
 * draft without the block. Every feature defaults to off. Keep them in sync with the C#.
 */

export const vehicleNoiseDefaults = (): Required<VehicleNoiseDef> => ({
  idleM: 0,
  fullM: 0,
  hornM: 0,
  hornCooldownS: 3,
});
export const vehicleFuelDefaults = (): Required<VehicleFuelDef> => ({
  tankS: 0,
  idleBurn: 0.2,
  electric: false,
});
export const vehicleRamDefaults = (): Required<VehicleRamDef> => ({
  minSpeedMps: 0,
  damageTaken: 0,
  speedKeep: 1,
  bruteDamage: 0,
  hitDamage: 0,
  hitSpeedLoss: 0,
});
export const vehicleClingDefaults = (): Required<VehicleClingDef> => ({
  max: 6,
  shakeTurnDegPerS: 90,
  shakeMinSpeedMps: 4,
});

export const vehicleDefaults = (id = ''): Required<VehicleDef> => ({
  id,
  name: '',
  nickname: '',
  model: '',
  roadKmh: 0,
  tripEnergyPerKm: 0,
  unlockAfterLureRuns: 0,
  seats: [],
  topSpeedMps: 0,
  accelMps2: 0,
  turnDegPerS: 0,
  maxHealth: 0,
  radiusM: 0,
  cargoKg: 0,
  noise: vehicleNoiseDefaults(),
  fuel: vehicleFuelDefaults(),
  ram: vehicleRamDefaults(),
  cling: vehicleClingDefaults(),
  mount: null,
  lendsWeapons: {},
});

/** A new rung for the editor's Add: a two-seat bike with the page's e-bike numbers, so the preview has something to draw. */
export const newVehicle = (id: string): Required<VehicleDef> => ({
  ...vehicleDefaults(id),
  name: 'New vehicle',
  roadKmh: 15,
  tripEnergyPerKm: 10,
  seats: [
    { role: 'driver', arcDeg: 360 },
    { role: 'pillion', arcDeg: 360 },
  ],
  topSpeedMps: 9.5,
  accelMps2: 6,
  turnDegPerS: 220,
  maxHealth: 90,
  radiusM: 0.9,
  cargoKg: 6,
  noise: { idleM: 4, fullM: 8, hornM: 55, hornCooldownS: 3 },
  fuel: { tankS: 200, idleBurn: 0.2, electric: true },
  ram: { ...vehicleRamDefaults(), hitDamage: 8, hitSpeedLoss: 0.4 },
});

export const arenaTemplateDefaults = (id = ''): Required<ArenaTemplateDef> => ({
  id,
  name: '',
  categories: [],
  sizeM: [],
  cellM: 2,
  blocks: [],
  wreckSlots: [],
  fuelCanSlots: [],
  objective: { at: { x: 0, y: 0 }, kind: 'supply_truck' },
  start: { x: 0, y: 0 },
  exit: { x: 0, y: 0, w: 0, h: 0, kind: '' },
  spawn: [],
  reinforceFrom: [],
});

/** A new template for the editor's Add: one block between the start and a truck, an exit in the far corner. */
export const newArenaTemplate = (id: string): Required<ArenaTemplateDef> => ({
  ...arenaTemplateDefaults(id),
  name: 'New arena',
  categories: ['supply_cache'],
  sizeM: [220, 160],
  blocks: [{ x: 80, y: 50, w: 50, h: 40, kind: 'strip_mall' }],
  fuelCanSlots: [
    { x: 60, y: 30 },
    { x: 150, y: 120 },
  ],
  objective: { at: { x: 160, y: 70 }, kind: 'supply_truck' },
  start: { x: 8, y: 60 },
  exit: { x: 200, y: 4, w: 14, h: 14, kind: '' },
  spawn: [
    { around: { x: 160, y: 70 }, radiusM: 26, streets: false, share: 0.7 },
    { around: null, radiusM: 0, streets: true, share: 0.3 },
  ],
  reinforceFrom: ['north', 'east'],
});

export const arenaGrabDefaults = (): Required<ArenaGrabDef> => ({
  radiusM: 8,
  stopMps: 1.2,
  baseS: 2.5,
  perKgS: 0.03,
});
export const arenaCrowdDefaults = (): Required<ArenaCrowdDef> => ({
  heroClingers: 6,
  simHz: 30,
  farSimHz: 10,
  flowHz: 4,
  hullWithinM: 25,
});
export const arenaRulesDefaults = (): Required<ArenaRulesDef> => ({
  timeLimitS: 360,
  hordeCap: 300,
  reinforceMaxShare: 0.25,
  qualityTiers: [],
  maxBrutes: 3,
  grab: arenaGrabDefaults(),
  pickupS: 4,
  fuelCanS: 50,
  loseTrackM: 90,
  loseTrackS: 10,
  clingDps: {},
  crowd: arenaCrowdDefaults(),
  startTankShare: 0.7,
  runnerShare: 1 / 6,
  brutesPer100: 1,
  minRunSlack: 0.85,
});
/** shared-cs `ArenaRulesDef.DefaultQualityTiers` (high, mid, low), used when `qualityTiers` is empty (`TierCaps()`). */
export const DEFAULT_QUALITY_TIERS = [300, 220, 150] as const;

export const lureSitesDefaults = (): Required<LureSiteRulesDef> => ({
  enabled: false,
  chancePerSet: 0.5,
  maxPerSet: 1,
  windowH: 4,
  minDistanceM: 600,
  strandedShare: 0.25,
  categories: [],
  minFootprint: 'large',
  hordeByTier: {},
  startUnlocked: [],
  vehicleWeights: {},
  overrunShare: 0.25,
  cargoLootTableId: '',
  cargoRolls: 8,
  strandedNames: [],
  strandedHealthShare: 0.75,
  // WIND (contract 3.7): the stranded survivor's start tier; strandedHealthShare is the old field, kept one version.
  strandedTier: 'winded',
});

/** S3-ARS `VehicleMountDef` initializers (the weapon is a plain WeaponDef of overrides). */
export const vehicleMountDefaults = (): Required<VehicleMountDef> => ({
  id: '',
  seat: 'mount',
  weapon: {},
  roundsPerRun: 0,
  baseItemId: 'lmg',
  noiseM: 0,
});

/** shared-cs `WeaponDef` initializers that aren't 0, false or null (the mount merge compares against them). */
export const WEAPON_DEFAULTS = {
  pellets: 1,
  pierceFalloff: 0.7,
  knockbackM: 0.25,
  stealthKillS: 0.4,
  bloomRecoverDegPerS: 20,
} as const;

/** S3-ARS `ScenarioArenaDef` initializers. */
export const scenarioArenaDefaults = (): Required<ScenarioArenaDef> => ({
  templateId: '',
  horde: {},
  vehicles: [],
  objectiveKind: '',
  pickupId: '',
  timeLimitS: 0,
  gateOpensAfterS: 0,
  cargoLootTableId: '',
  cargoRolls: 0,
});

export const overwatchRulesDefaults = (): Required<OverwatchRulesDef> => ({
  enabled: false,
  fillPerM: 0.25,
  decayPerS: 0.5,
  listenAt: 0.5,
  relocateNoiseTo: 0.4,
  relocateCostS: 10,
  maxRelocations: 1,
  hitToleranceM: 0.05,
  gravityMult: 1,
  simHz: 20,
  aimTrackHz: 5,
  maxAimRadPerS: 6,
  minVisibleS: 0.2,
  bodyRadiusM: 0.3,
  bodyHeightM: 1.5,
  headRadiusM: 0.17,
  headHeightM: 1.64,
  bystanderFleeM: 25,
  bystanderFleeS: 3,
  perchTags: [],
  minLevels: 6,
  minHeightM: 18,
  perchCooldownH: 6,
  maxPerSet: 1,
  importDensityMax: 2,
  importDensityRadiusM: 2000,
  perchMissionId: '',
  energy: 8,
  perchChancePerSet: 1,
  crateRolls: [],
  oneMoreShotCondition: 'wounded',
});

export const overwatchDefaults = (): Required<OverwatchDef> => ({
  rules: overwatchRulesDefaults(),
  scenes: [],
  figures: [],
  traits: {},
  missions: [],
});

export const overwatchSceneDefaults = (id = ''): Required<OverwatchSceneDef> => ({
  id,
  name: '',
  prefab: '',
  perches: [],
  plan: { zones: {}, paths: {}, exits: [], occluders: [] },
});
export const overwatchPerchDefaults = (id = ''): Required<OverwatchPerchDef> => ({
  id,
  name: '',
  pos: [0, 18, 0],
  yawDeg: [-35, 35],
  pitchDeg: [-25, 2],
  foreground: '',
});
export const overwatchFigureDefaults = (id = ''): Required<OverwatchFigureDef> => ({
  id,
  name: '',
  kind: 'human',
  zombieId: '',
  hp: 100,
  role: 'innocent',
  walkMps: 1.3,
  runMps: 3.5,
  reveal: null,
  threat: null,
});
export const overwatchSpawnDefaults = (): Required<OverwatchSpawnDef> => ({
  figure: '',
  count: 1,
  routine: 'wander',
  zone: '',
  path: '',
});
export const overwatchPhaseDefaults = (id = ''): Required<OverwatchPhaseDef> => ({
  id,
  timeS: 60,
  objective: {
    type: 'kill_target',
    describe: [],
    decoys: 0,
    fleeOnMissWithinM: 0,
    hidden: '',
    figure: '',
    path: '',
  },
  spawns: [],
  hidden: {},
  decoyTells: [],
  waves: null,
  revealBeat: '',
});
export const overwatchMissionDefaults = (id = ''): Required<OverwatchMissionDef> => ({
  id,
  name: '',
  sceneId: '',
  perchId: '',
  altPerchId: '',
  shotCap: 0,
  requiredWeaponIds: [],
  briefingBeat: '',
  phases: [],
  noKill: 'soft',
  rewardLootTable: '',
  grades: [],
});

/** The Step 3 sections with C# defaults (merged into `teamsSectionDefaults`), shown for a draft without them. */
export const s3SectionDefaults = {
  arenaRules: arenaRulesDefaults,
  lureSites: lureSitesDefaults,
  overwatch: overwatchDefaults,
} as const;
